import Axios, { AxiosError, AxiosRequestConfig, AxiosRequestHeaders } from "axios";

import { HttpStatus } from "entities/common";
import { API_URL, TOKEN_KEY } from "./constants";
import { CookieHelper } from "./helpers";
import { PAGES } from "./links";

interface CustomAxiosConfig extends AxiosRequestConfig {
  skipRedirects?: boolean;
  skipAuthRedirect?: boolean;
}

const defaultConfig = {
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
};

export function normalizeToken(token = ""): string {
  return token.startsWith("Bearer ") ? token : `Bearer ${token}`;
}

const http = Axios.create(defaultConfig);

http.interceptors.request.use(
  (config) => {
    const token = CookieHelper.getCookie(TOKEN_KEY);

    config.headers = {
      ...config.headers,
      ...(token ? { Authorization: normalizeToken(token) } : {}),
    } as AxiosRequestHeaders;

    return config;
  },
  (error) => Promise.reject(error),
);

http.interceptors.response.use(
  (response) => {
    return Promise.resolve(response);
  },
  (error: AxiosError<unknown>) => {
    const config = error.config as CustomAxiosConfig | undefined;

    if (error.response?.status === HttpStatus.Unauthorized) {
      if (config?.skipAuthRedirect) {
        return Promise.reject(error?.response?.data);
      }

      CookieHelper.deleteCookie(TOKEN_KEY, { path: "/" });
      window.location.href = PAGES.LOGIN;
      return Promise.reject();
    } else if (error.response?.status === HttpStatus.Forbidden) {
      if (!config?.skipAuthRedirect) {
        window.location.href = PAGES.FORBIDDEN;
      }
      return Promise.reject(error?.response?.data);
    } else if (error.response?.status === HttpStatus.NotFound) {
      if (!config?.skipAuthRedirect) {
        window.location.replace(PAGES.NOT_FOUND);
      }
      return Promise.reject(error?.response?.data);
    } else if (error.response?.status === HttpStatus.BadRequest) {
      // if (!config?.skipAuthRedirect) {
      //   window.location.replace(PAGES.BAD_REQUEST);
      // }
      return Promise.reject(error?.response?.data);
    } else if (error.response?.status === HttpStatus.InternalServerError) {
      window.location.replace(PAGES.SERVER_ERROR);
      return Promise.reject(error?.response?.data);
    } else if (
      error.response?.status === HttpStatus.ManyRequests ||
      error.response?.status === HttpStatus.LargeRequest
    ) {
      return Promise.reject(error.response?.data as string);
    } else if (error.response?.status === HttpStatus.UnprocessableEntity) {
      return error.response?.data;
    }

    return Promise.reject(error?.response?.data);
  },
);

function GET<T = unknown>(url: string, config: CustomAxiosConfig = {}) {
  return http.get<T>(url, {
    ...config,
    headers: {
      ...config?.headers,
    },
  });
}

function POST<TBody = unknown, TResponse = unknown>(url: string, body?: TBody, config: CustomAxiosConfig = {}) {
  return http.post<TResponse>(url, body, {
    ...config,
    headers: {
      ...config?.headers,
    },
  });
}

function PUT<T>(url: string, body: T, config: CustomAxiosConfig = {}) {
  return http.put(url, body, {
    ...config,
    headers: {
      ...config?.headers,
    },
  });
}

function PATCH<TBody = unknown, TResponse = unknown>(url: string, body?: TBody, config: CustomAxiosConfig = {}) {
  return http.patch<TResponse>(url, body, {
    ...config,
    headers: {
      ...config?.headers,
    },
  });
}

function DELETE(url: string, config: CustomAxiosConfig = {}) {
  return http.delete(url, {
    ...config,
    headers: {
      ...config?.headers,
    },
  });
}

export { DELETE, GET, PATCH, POST, PUT };

export default http;
